/**
 * Phase S1 — Recent Setting Changes card.
 *
 * Wraps GET /api/admin/audit/setting-changes. Renders a compact list with:
 * - When / Who / What columns
 * - Expandable details_json (pretty JSON) under each row
 * - Show-more pagination (50 at a time, capped at 200 by the backend)
 *
 * The backend already masks secret-shaped fields (api_key / secret /
 * password / *_ciphertext) → "****<last4>". This component does not
 * unmask anything; it just renders whatever it gets.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { History, ChevronRight } from "lucide-react";
import { toast } from "sonner";

function formatTimestamp(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const PAGE = 50;
const MAX = 200;

function EventTypePill({ type }) {
    const label = String(type || "settings_updated").replace(/_/g, " ");
    const tone = type?.startsWith("tenant_")
        ? "bg-blue-50 text-blue-800 ring-blue-100"
        : type?.startsWith("stripe_")
            ? "bg-violet-50 text-violet-800 ring-violet-100"
            : type?.startsWith("resend_") || type?.startsWith("email_")
                ? "bg-emerald-50 text-emerald-800 ring-emerald-100"
                : "bg-slate-100 text-slate-700 ring-slate-200";
    return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase ring-1 ring-inset ${tone}`}>
            {label}
        </span>
    );
}

function Row({ item }) {
    const [open, setOpen] = useState(false);
    const details = item.details_json || {};
    const changes = Array.isArray(details.changes) ? details.changes : [];
    return (
        <li
            className="rounded-md border border-slate-200 bg-white"
            data-testid={`setting-change-row-${item.id}`}
        >
            <button
                type="button"
                className="w-full px-3 py-2.5 flex items-start gap-3 text-left hover:bg-slate-50 transition-colors"
                onClick={() => setOpen((v) => !v)}
                data-testid={`setting-change-toggle-${item.id}`}
            >
                <ChevronRight
                    size={14}
                    className={`mt-1 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`}
                />
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span>{formatTimestamp(item.occurred_at)}</span>
                        <span className="text-slate-300">·</span>
                        <span className="font-mono text-[11px] truncate">
                            {item.actor_name || item.actor_id || "system"}
                        </span>
                        {item.actor_role && (
                            <span className="text-[10px] uppercase tracking-wider text-slate-400">
                                {item.actor_role}
                            </span>
                        )}
                        <EventTypePill type={item.event_type} />
                    </div>
                    <div className="mt-1 text-sm text-slate-900 break-words">
                        {item.summary}
                    </div>
                </div>
            </button>
            {open && (
                <div
                    className="px-3 pb-3 pt-1 border-t border-slate-100 bg-slate-50/40"
                    data-testid={`setting-change-details-${item.id}`}
                >
                    {changes.length === 0 ? (
                        <pre className="text-[11px] font-mono text-slate-600 whitespace-pre-wrap">
                            {JSON.stringify(details, null, 2)}
                        </pre>
                    ) : (
                        <ul className="space-y-1.5">
                            {changes.map((c, idx) => (
                                <li
                                    key={idx}
                                    className="text-xs flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5"
                                >
                                    <span className="font-medium text-slate-900 break-all">
                                        {c.field}
                                    </span>
                                    <span className="text-slate-400">·</span>
                                    <span className="font-mono text-[11px] text-slate-500 break-all">
                                        {JSON.stringify(c.old)}
                                    </span>
                                    <span className="text-slate-400">→</span>
                                    <span className="font-mono text-[11px] text-slate-900 break-all">
                                        {JSON.stringify(c.new)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </li>
    );
}

export function RecentSettingChangesCard() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [limit, setLimit] = useState(PAGE);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async (nextLimit) => {
        setLoading(true);
        try {
            const lim = Math.min(nextLimit ?? limit, MAX);
            const { data } = await api.get(`/admin/audit/setting-changes?limit=${lim}`);
            setItems(data?.items || []);
            setTotal(data?.total || 0);
            setLimit(lim);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [limit]);

    useEffect(() => { load(PAGE); /* initial */ }, []); // eslint-disable-line

    const hasMore = items.length < total && limit < MAX;

    return (
        <Card className="lg:col-span-2" data-testid="recent-setting-changes-card">
            <CardHeader>
                <div className="flex items-center gap-2">
                    <History size={16} className="text-blue-900" />
                    <CardTitle className="font-display text-xl">Recent setting changes</CardTitle>
                </div>
                <CardDescription>
                    Edits to Tenant Identity, Integrations, and Default Templates are
                    captured here. Secrets are masked to last 4 characters.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {loading && items.length === 0 ? (
                    <div
                        className="text-sm text-slate-500"
                        data-testid="recent-setting-changes-loading"
                    >
                        Loading…
                    </div>
                ) : items.length === 0 ? (
                    <div
                        className="text-sm text-slate-500"
                        data-testid="recent-setting-changes-empty"
                    >
                        No setting changes yet. Edits to Tenant Identity, Integrations,
                        and Default Templates will appear here.
                    </div>
                ) : (
                    <>
                        <ul
                            className="space-y-2"
                            data-testid="recent-setting-changes-list"
                        >
                            {items.map((it) => <Row key={it.id} item={it} />)}
                        </ul>
                        {hasMore && (
                            <div className="flex justify-center mt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    data-testid="setting-changes-load-more"
                                    onClick={() => load(Math.min(limit + PAGE, MAX))}
                                    disabled={loading}
                                >
                                    {loading ? "Loading…" : `Show ${Math.min(PAGE, total - items.length)} more`}
                                </Button>
                            </div>
                        )}
                        {!hasMore && items.length >= MAX && (
                            <p className="text-xs text-slate-500 mt-4 text-center">
                                Showing the {MAX} most recent — older changes are still in
                                the audit log but capped here for performance.
                            </p>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}

export default RecentSettingChangesCard;
